/*   This is the main landing page
- Users will be greated with a welcome message
- Asked to login if not already
- Guided through selecting a form and completing it
*/

"use client";

import AuthButton from "@/components/Auth/AuthButton";
import Link from "next/link";
import useAuthorization from "@/hooks/useAuth";
import SpinnerCheck from "@/components/spinner-check";
import { useState } from "react";
import {
  ChevronLeft,
  CircleUser,
  CircleX,
  ClipboardList,
  Headset,
  Settings,
  UserPen,
  UserPlus,
  Users,
  UserX,
  Wifi,
} from "lucide-react";
import CancelButton from "@/components/cancel-button";

const PAGE_CENTER_CLASS = "flex h-full flex-col items-center justify-center px-6 mb-0 pb-0";
const TITLE_BLOCK_CLASS = "h-100 w-100 text-center";
const MENU_ZONE_CLASS =
  "flex min-h-[300px] w-full max-w-[450px] flex-col items-center justify-center gap-4 p-8 mx-auto";
const MENU_CARD_CLASS =
  "flex w-full items-center rounded-xl border p-4 text-left hover:cursor-pointer hover:bg-gray-100 transform transition duration-200 hover:scale-105";
const MENU_ICON_CLASS = "ml-4 mr-6 h-12 w-10 shrink-0 text-primary";

export default function Home() {
  const { isAuthorized, session, isLoaded, location, isGazelle } = useAuthorization();

  const [showStaffRequests, setShowStaffRequests] = useState(false);
  const [showClientRequests, setShowClientRequests] = useState(false);

  if (!isLoaded && !session) {
    return (
      <div className="flex h-full flex-1 flex-col items-center justify-center">
        <SpinnerCheck size={"75px"} />
        <h2 className="my-4 text-2xl font-semibold">Logging in...</h2>
      </div>
    );
  }

  if (showStaffRequests) return <StaffRequestsMenu />;
  if (showClientRequests) return <ClientRequestsMenu />;

  if (isLoaded && !session) {
    return (
      <div className="flex h-full flex-1 flex-col items-center justify-center px-6 pb-20 space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-semibold">Welcome!</h1>
          <h4 className="text-lg font-semibold">Please sign in to continue</h4>
        </div>

        <div className="flex w-full flex-col items-center">
          <div className="w-1/2">
            <AuthButton />
          </div>
        </div>
      </div>
    );
  }

  if (isLoaded && session) {
    return (
      <div className={PAGE_CENTER_CLASS}>
        <div className={TITLE_BLOCK_CLASS}>
          <h1 className="text-4xl font-semibold">Welcome {session?.user.givenName}!</h1>
          <h4 className="text-lg font-semibold">What would you like to get started on?</h4>
        </div>

        <div className={MENU_ZONE_CLASS}>
          <Link href="/support" className="block w-full">
            <div className={MENU_CARD_CLASS}>
              <Headset className={MENU_ICON_CLASS} />
              <div className="flex-1">
                <p className="text-lg font-semibold">Support Request</p>
                <p className="text-sm text-muted-foreground">
                  Request technical assistance from the Gazelle Support team
                </p>
              </div>
            </div>
          </Link>

          {isAuthorized && location && (
            <button type="button" onClick={() => setShowStaffRequests(true)} className={MENU_CARD_CLASS}>
              <Users className={MENU_ICON_CLASS} />
              <div className="flex-1">
                <p className="text-lg font-semibold">Staff Requests</p>
                <p className="text-sm text-muted-foreground">
                  Submit requests to Create, Update or Disable IO Staff accounts.
                </p>
              </div>
            </button>
          )}

          {isGazelle && (
            <Link href="/admin" className="block w-full">
              <div className={MENU_CARD_CLASS}>
                <Settings className={MENU_ICON_CLASS} />
                <div className="flex-1">
                  <p className="text-lg font-semibold">Admin Panel</p>
                  <p className="text-sm text-muted-foreground">
                    Configure locations, WiFi profiles, and other backend settings.
                  </p>
                </div>
              </div>
            </Link>
          )}

          {isAuthorized && (
            <button type="button" onClick={() => setShowClientRequests(true)} className={MENU_CARD_CLASS}>
              <CircleUser className={MENU_ICON_CLASS} />
              <div className="flex-1">
                <p className="text-lg font-semibold">Client Services</p>
                <p className="text-sm text-muted-foreground">
                  Orders, service deactivation, and other client-related requests.
                </p>
              </div>
            </button>
          )}

          {isGazelle && location && (
            <Link href="/wifi" className="block w-full">
              <div className={MENU_CARD_CLASS}>
                <Wifi className={MENU_ICON_CLASS} />
                <div className="flex-1">
                  <p className="text-lg font-semibold">Office WiFi</p>
                  <p className="text-sm text-muted-foreground">
                    View WiFi passwords and request updates for your location.
                  </p>
                </div>
              </div>
            </Link>
          )}
        </div>
      </div>
    );
  }

  return null;
}

function StaffRequestsMenu() {
  return (
    <div className={PAGE_CENTER_CLASS}>
      <div className={TITLE_BLOCK_CLASS}>
        <h1 className="text-4xl font-semibold">Staff User Requests</h1>
        <h4 className="text-lg font-semibold">What would you like to get started on?</h4>
      </div>

      <div className={MENU_ZONE_CLASS}>
        <Link href="/staff/create" className="block w-full">
          <div className={MENU_CARD_CLASS}>
            <UserPlus className={MENU_ICON_CLASS} />
            <div className="flex-1">
              <p className="text-lg font-semibold">Add New Staff</p>
              <p className="text-sm text-muted-foreground">
                Request to create of one or more Intelligent Office staff users
              </p>
            </div>
          </div>
        </Link>

        <Link href="/staff/update" className="block w-full">
          <div className={MENU_CARD_CLASS}>
            <UserPen className={MENU_ICON_CLASS} />
            <div className="flex-1">
              <p className="text-lg font-semibold">Update Existing Staff</p>
              <p className="text-sm text-muted-foreground">
                Request changes to one or more Intelligent Office staff users
              </p>
            </div>
          </div>
        </Link>

        <Link href="/staff/disable" className="block w-full">
          <div className={MENU_CARD_CLASS}>
            <UserX className={MENU_ICON_CLASS} />
            <div className="flex-1">
              <p className="text-lg font-semibold">Disable Staff</p>
              <p className="text-sm text-muted-foreground">
                Request to disable one or more Intelligent Office staff users
              </p>
            </div>
          </div>
        </Link>
      </div>

      <div className="mx-auto mt-2 mb-4">
        <CancelButton variant="outline">
          <ChevronLeft /> Go Back
        </CancelButton>
      </div>
    </div>
  );
}

function ClientRequestsMenu() {
  return (
    <div className={PAGE_CENTER_CLASS}>
      <div className={TITLE_BLOCK_CLASS}>
        <h1 className="text-4xl font-semibold">Client Services</h1>
        <h4 className="text-lg font-semibold">What would you like to get started on?</h4>
      </div>

      <div className={MENU_ZONE_CLASS}>
        <Link href="/orders" className="block w-full">
          <div className={MENU_CARD_CLASS}>
            <ClipboardList className={MENU_ICON_CLASS} />
            <div className="flex-1">
              <p className="text-lg font-semibold">New Service Request</p>
              <p className="text-sm text-muted-foreground">
                Request a new subscription, service or product on behalf of a client.
              </p>
            </div>
          </div>
        </Link>

        <Link href="/deactivate-service" className="block w-full">
          <div className={MENU_CARD_CLASS}>
            <CircleX className={MENU_ICON_CLASS} />
            <div className="flex-1">
              <p className="text-lg font-semibold">Service Deactivation</p>
              <p className="text-sm text-muted-foreground">
                Request the cancellation of a subscription, service, or product on behalf of a client.
              </p>
            </div>
          </div>
        </Link>
      </div>

      <div className="mx-auto mt-2 mb-4">
        <CancelButton variant="outline">
          <ChevronLeft /> Go Back
        </CancelButton>
      </div>
    </div>
  );
}
